import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

export default function WhyChooseUs() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const reasons = [
    {
      step: '01',
      icon: 'ri-time-line',
      title: 'Fast Hiring Process',
      description: 'Submit a brief and receive matched, pre-vetted candidates within 48 hours — not weeks.',
      stat: '48h',
      statLabel: 'avg. turnaround',
      accentColor: 'bg-primary-500',
      accentLight: 'bg-primary-50',
      accentBorder: 'border-primary-200',
      accentText: 'text-primary-600',
      accentGlow: 'bg-primary-200',
    },
    {
      step: '02',
      icon: 'ri-exchange-dollar-line',
      title: 'Flexible Engagement Models',
      description: 'Staff augmentation, dedicated teams, or fixed-scope projects — switch models as your needs evolve.',
      stat: '3',
      statLabel: 'models',
      accentColor: 'bg-amber-500',
      accentLight: 'bg-amber-50',
      accentBorder: 'border-amber-200',
      accentText: 'text-amber-600',
      accentGlow: 'bg-amber-200',
    },
    {
      step: '03',
      icon: 'ri-user-star-line',
      title: 'Skilled & Experienced Talent',
      description: 'Only 12% of applicants pass our vetting. Every engineer has 5+ years of production experience.',
      stat: '12%',
      statLabel: 'acceptance rate',
      accentColor: 'bg-emerald-500',
      accentLight: 'bg-emerald-50',
      accentBorder: 'border-emerald-200',
      accentText: 'text-emerald-600',
      accentGlow: 'bg-emerald-200',
    },
    {
      step: '04',
      icon: 'ri-bar-chart-grouped-line',
      title: 'Cost-Effective Scaling',
      description: 'Save up to 60% on fully-loaded engineering costs while maintaining the same code quality and velocity.',
      stat: '60%',
      statLabel: 'avg. savings',
      accentColor: 'bg-sky-500',
      accentLight: 'bg-sky-50',
      accentBorder: 'border-sky-200',
      accentText: 'text-sky-600',
      accentGlow: 'bg-sky-200',
    },
    {
      step: '05',
      icon: 'ri-team-line',
      title: 'Seamless Team Integration',
      description: 'Engineers join your standups, commit to your repos, and use your tools — like they were always there.',
      stat: '14d',
      statLabel: 'replacement guarantee',
      accentColor: 'bg-rose-500',
      accentLight: 'bg-rose-50',
      accentBorder: 'border-rose-200',
      accentText: 'text-rose-600',
      accentGlow: 'bg-rose-200',
    },
    {
      step: '06',
      icon: 'ri-customer-service-2-line',
      title: 'Dedicated Support',
      description: 'A dedicated account lead and HR partner for every engagement. Transparent communication, zero surprises.',
      stat: '24/7',
      statLabel: 'support coverage',
      accentColor: 'bg-violet-500',
      accentLight: 'bg-violet-50',
      accentBorder: 'border-violet-200',
      accentText: 'text-violet-600',
      accentGlow: 'bg-violet-200',
    },
  ];

  return (
    <section id="why-choose-us" className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 right-0 w-96 h-96 bg-primary-100/40 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-0 w-80 h-80 bg-amber-100/30 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-shield-check-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Why Choose Us</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6 mb-3"
          >
            Why Businesses Choose
            <br />
            <span className="text-primary-600">Thoughtic Solutions</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            From seed-stage startups to Fortune 500 enterprises, we provide the talent, process, and support to deliver faster and scale with confidence.
          </motion.p>

          {/* Stats bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="mt-8 md:mt-10 flex flex-wrap items-center justify-center gap-6 md:gap-10"
          >
            {[
              { value: '85+', label: 'Engineers Deployed' },
              { value: '10+', label: 'Enterprise Clients' },
              { value: '4.9/5', label: 'Client Satisfaction' },
              { value: '60%', label: 'Avg. Cost Savings' },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-xl md:text-2xl font-bold text-navy-900">{s.value}</div>
                <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Reasons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ ...springTransition, duration: 0.7, delay: index * 0.08 }}
              className="group relative bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl hover:shadow-gray-900/5 transition-all duration-500 hover:-translate-y-1"
            >
              {/* Accent top bar */}
              <div className={`h-1 w-full ${reason.accentColor}`}></div>

              <div className="p-6 md:p-7 relative">
                {/* Step number */}
                <span className={`absolute top-4 right-4 text-5xl font-bold ${reason.accentText} opacity-[0.07] font-display select-none`}>
                  {reason.step}
                </span>

                <div className="flex items-start gap-4 mb-4">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: index * 0.08 + 0.1, ...springTransition }}
                    className={`w-12 h-12 flex items-center justify-center rounded-xl ${reason.accentLight} border ${reason.accentBorder} flex-shrink-0 group-hover:scale-110 transition-transform duration-500`}
                  >
                    <i className={`${reason.icon} text-xl ${reason.accentText}`}></i>
                  </motion.div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${reason.accentText}`}>{reason.step}</span>
                      <span className="text-[10px] font-semibold text-gray-400">|</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold ${reason.accentLight} ${reason.accentText} border ${reason.accentBorder}`}>
                        {reason.stat} {reason.statLabel}
                      </span>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-navy-900 leading-tight">
                      {reason.title}
                    </h3>
                  </div>
                </div>

                <motion.p
                  initial={{ opacity: 0, y: 15 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.08 + 0.2, duration: 0.5 }}
                  className="text-sm text-gray-600 leading-relaxed"
                >
                  {reason.description}
                </motion.p>
              </div>

              {/* Background glow */}
              <div className={`absolute -bottom-16 -right-16 w-48 h-48 ${reason.accentGlow} rounded-full blur-3xl opacity-20 pointer-events-none group-hover:opacity-35 transition-opacity duration-500`}></div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.7, ...springTransition }}
          className="mt-12 md:mt-16"
        >
          <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-lg md:text-xl font-bold text-navy-900 mb-1">Ready to see the difference?</h3>
              <p className="text-sm text-gray-500">Talk to our team and get matched candidates within 48 hours — no commitment required.</p>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-3 flex-shrink-0">
              <Link
                to="/talent"
                className="px-6 py-3 bg-primary-500 text-white text-sm font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
              >
                Browse Talent
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/how-it-works"
                className="px-6 py-3 border border-gray-200 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                See How It Works
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}